import { POSCartItem, POSPaymentMethod } from '../types/pos';
import { completePOSTransaction } from '../services/posService';
import {
  listOfflineSales,
  updateOfflineSale,
  type OfflineQueuedSale,
} from './posOfflineStore';

export type OfflineSyncResult = {
  synced: number;
  failed: number;
  remaining: number;
};

function isAuthError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
}

function errorMessage(err: unknown): string {
  const raw = (err as { response?: { data?: { detail?: unknown } }; message?: string })?.response?.data
    ?.detail;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0];
    if (typeof first === 'object' && first && 'msg' in first) {
      return String((first as { msg: string }).msg);
    }
  }
  const msg = (err as { message?: string })?.message;
  return msg && msg.trim() ? msg.trim() : 'Sync failed';
}

export async function syncOfflineSalesQueue(): Promise<OfflineSyncResult> {
  const pending = await listOfflineSales('pending');
  if (pending.length === 0) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  let synced = 0;
  let failed = 0;

  for (const sale of pending) {
    try {
      const cart: POSCartItem[] = sale.items.map((item, idx) => ({
        id: `sync-${sale.local_id}-${idx}`,
        product_id: item.product_id,
        product_code: item.product_code,
        product_name: item.product_name,
        barcode: item.barcode,
        quantity: item.quantity,
        unit_price: item.unit_price,
        sale_unit_price: item.unit_price,
        total_price: item.total_price,
      }));

      const txn = await completePOSTransaction(
        cart,
        {
          method: sale.payment_method as POSPaymentMethod,
          amount: sale.total_amount,
          reference_number: sale.payment_reference || undefined,
          payer_phone: sale.payer_phone || undefined,
        },
        sale.location_id,
        {
          tax_amount: sale.tax_amount,
          client_sale_id: sale.local_id,
          buyer_name: sale.buyer_name || undefined,
          buyer_cid: sale.buyer_cid || undefined,
          buyer_tpn: sale.buyer_tpn || undefined,
          buyer_address: sale.buyer_address || undefined,
          receipt_printed: sale.receipt_printed !== false,
        },
      );

      await updateOfflineSale(sale.local_id, {
        status: 'synced',
        server_transaction_id: txn.id,
        server_transaction_number: txn.transaction_number,
        sync_error: undefined,
      });
      synced += 1;
    } catch (err) {
      if (isAuthError(err)) {
        throw err;
      }
      await updateOfflineSale(sale.local_id, {
        status: 'failed',
        sync_error: errorMessage(err),
      });
      failed += 1;
    }
  }

  const remaining = (await listOfflineSales('pending')).length;
  return { synced, failed, remaining };
}
